import * as React from 'react';
import {Device} from "@/store/Device";
import {NumberTable} from "@/component/style/NumberTable";
import {Money} from "@/component/style/Money";
import {FtCard} from "@/FtStyle";

export interface TransactionListViewItem {
    key: string;
    date: string;
    type: string;
    inAmount: number;
    outAmount: number;
    detail: string;
    locked?: boolean;
    onClick?: () => void;
}

interface Props {
    prevBalance?: number;
    splitInOut: boolean;
    reverseInOut?: boolean;
    items: TransactionListViewItem[];
}

export function TransactionListView(props: Props) {
    const sum = props.items.reduce((acc, item) => acc + item.inAmount - item.outAmount, 0);
    if (Device.isMobile) {
        return <MobileView sum={sum} {...props}/>;
    } else {
        return <DesktopView {...props}/>;
    }
}

function MobileView(props: {sum: number} & Props) {
    const {prevBalance, items, reverseInOut} = props;
    let runningBalance = prevBalance ?? 0;
    const factor = reverseInOut ? -1 : 1;

    return <div style={{display: "flex", flexDirection: "column", gap: 8}}>

        <div style={{display: "flex", justifyContent: "space-between", fontSize: 15}}>
            <div>합계: <span style={{fontWeight: "bold"}}><Money amount={factor * props.sum}/></span></div>
            {prevBalance !== undefined && <div>잔액: <span style={{fontWeight: "bold"}}><Money amount={runningBalance + props.sum}/></span></div>}
        </div>

        {items.map((item) => {
            const amount = (item.inAmount - item.outAmount) * factor;
            runningBalance += item.inAmount - item.outAmount;

            const color = amount > 0 ? 'blue' : 'red';

            return <FtCard key={item.key}
                           style={{cursor: item.onClick ? 'pointer' : 'default'}}
                           onClick={item.onClick}>
                <div style={{display: "flex", gap: 16}}>
                    <div style={{fontSize: 12}}>{item.date}{item.locked ? '🔒 ' : ''}</div>
                    <div style={{fontSize: 12}}>{item.type}</div>
                    <div style={{flexGrow: 1, textAlign: 'right', fontSize: 15, color: color}}>
                        <Money amount={amount}/>
                    </div>
                </div>

                <div style={{display: "flex", justifyContent: "space-between", gap: 16}}>
                    <div style={{fontSize: 12, color: "#999"}}>{item.detail}</div>
                    {prevBalance && (
                        <div style={{fontSize: 12, color: "#999"}}><Money amount={runningBalance}/></div>
                    )}
                </div>
            </FtCard>
        })}
    </div>
}

function DesktopView(props: Props) {
    const {prevBalance, splitInOut, reverseInOut, items} = props;

    let runningBalance = prevBalance ?? 0;
    let inSum = 0;
    let outSum = 0;
    const factor = reverseInOut ? -1 : 1;

    return <NumberTable>
        <thead>
        <tr>
            <th>날짜</th>
            <th>구분</th>
            <th>상세 내역</th>
            {splitInOut && <><th style={{textAlign: "right"}}>출금</th><th style={{textAlign: "right"}}>입금</th></>}
            {!splitInOut && <><th style={{textAlign: "right"}}>금액</th></>}

            {prevBalance !== undefined && <th style={{textAlign: "right"}}>잔액</th>}
        </tr>
        </thead>
        <tbody>
        {prevBalance !== undefined && (
            <tr className="carry">
                <td colSpan={splitInOut ? 5 : 4} style={{textAlign: 'center'}}>이전 잔액</td>
                <td>{prevBalance.toLocaleString()}</td>
            </tr>
        )}
        {items.map((item) => {
            runningBalance += item.inAmount - item.outAmount;
            inSum += item.inAmount;
            outSum += item.outAmount;
            return <tr key={item.key} style={{cursor: item.onClick ? 'pointer' : 'default'}}
                       onClick={item.onClick}>
                <td className="nobr" style={{textAlign: "center"}}>{item.date} {item.locked ? '🔒 ' : ''}</td>
                <td className="nobr" style={{textAlign: "center"}}>{item.type}</td>
                <td style={{textAlign: "center"}}>{item.detail}</td>

                {splitInOut && (<>
                    <td className="nobr" align="right"><Money amount={item.outAmount}/></td>
                    <td className="nobr" align="right"><Money amount={item.inAmount}/></td>
                </>)}
                {!splitInOut && (
                    <td className="nobr" align="right"><Money amount={factor * (item.inAmount - item.outAmount)}/></td>
                )}
                {prevBalance !== undefined && (
                    <td className="nobr" align="right"><Money amount={runningBalance}/></td>
                )}
            </tr>
        })}
        <tr className="total">
            <td colSpan={3} style={{textAlign: 'center'}}>합계</td>

            {splitInOut && (
                <>
                    <td align="right"><Money amount={outSum}/></td>
                    <td align="right"><Money amount={inSum}/></td>
                </>
            )}

            {!splitInOut && (
                <td align="right"><Money amount={(inSum - outSum) * factor}/></td>
            )}

            {prevBalance !== undefined && (
                <td align="right"><Money amount={runningBalance}/></td>
            )}
        </tr>
        </tbody>
    </NumberTable>;
}